import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import {Shield, Target, Award, Users, TrendingUp, Heart, CheckCircle2, ArrowRight} from 'lucide-react'
import { Link } from "react-router-dom"

function About() {
  const values = [
    { icon: <Shield className="w-6 h-6" />, title: "Integrity", description: "We operate with the highest ethical standards and transparency in everything we do." },
    { icon: <Heart className="w-6 h-6" />, title: "Care", description: "Every client and property receives personalized attention and dedicated protection." },
    { icon: <Award className="w-6 h-6" />, title: "Excellence", description: "We pursue continuous improvement and deliver superior security services." },
    { icon: <Users className="w-6 h-6" />, title: "Teamwork", description: "Collaboration and communication are at the heart of our success." },
  ]

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050A14" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #0A1224, #050A14)" }} />
        <div className="absolute top-0 left-0 w-full h-px" style={{ background: "linear-gradient(to right, transparent, rgba(100,116,139,0.3), transparent)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-6 block">
            About Sentrio Security
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6">
            Protecting Clients
            <br />
            <span className="text-slate-600">Since 2019</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl leading-relaxed">
            We are a trusted security company committed to delivering professional,
            reliable protection services across Canada and the USA. Your safety is our mission.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section style={{ backgroundColor: "#0A1224", borderTop: "1px solid #111D35", borderBottom: "1px solid #111D35" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {[
              { value: "7+", label: "Years of Experience" },
              { value: "500+", label: "Clients Protected" },
              { value: "50+", label: "Security Professionals" },
              { value: "99%", label: "Client Retention" },
            ].map((stat, i) => (
              <div key={i} className="py-10 sm:py-14 text-center" style={i < 3 ? { borderRight: "1px solid #111D35" } : undefined}>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-xs sm:text-sm text-slate-500 tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#050A14" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Target className="w-5 h-5 text-slate-400" />
                <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase">
                  Our Mission
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-8">
                Dedicated to
                <br />
                <span className="text-slate-600">Your Safety</span>
              </h2>
              <div className="space-y-5 text-slate-400 text-lg leading-relaxed">
                <p>
                  Sentrio Security was founded with a simple yet powerful mission: to provide
                  the highest quality security services that protect people, property, and
                  assets across North America.
                </p>
                <p>
                  For over 7 years, we have been the trusted security partner for residential
                  complexes, commercial properties, construction sites, retail businesses, and
                  special events throughout major cities across Canada and the USA.
                </p>
                <p>
                  Our team of highly trained, licensed security professionals brings experience,
                  dedication, and integrity to every assignment. We do not just provide guards
                  - we deliver peace of mind.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img
                  src="https://static.lumi.new/02/02c4b1ec445906af447f79b8dc37ea47.jpg"
                  alt="Security patrol car at night"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 border-l-2 border-b-2 rounded-bl-lg" style={{ borderColor: "rgba(100,116,139,0.3)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24" style={{ backgroundColor: "#0A1224" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16">
            <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Our Values
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              What We <span className="text-slate-600">Stand For</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, i) => (
              <div
                key={i}
                className="group p-8 rounded-lg hover:brightness-125 transition-all relative overflow-hidden"
                style={{ backgroundColor: "#050A14", border: "1px solid #111D35" }}
              >
                <div className="absolute top-0 left-0 w-0 group-hover:w-full h-px bg-gradient-to-r from-slate-400 to-slate-500 transition-all duration-500" />
                <div className="text-slate-600 group-hover:text-slate-300 mb-6 transition-colors">
                  {value.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{value.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#050A14" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img
                  src="https://static.lumi.new/5d/5d31433afde670710ac267780e6772ea.png"
                  alt="Security team training session"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 border-r-2 border-t-2 rounded-tr-lg" style={{ borderColor: "rgba(100,116,139,0.3)" }} />
            </div>
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-5 h-5 text-slate-400" />
                <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase">
                  Training & Standards
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-8">
                Highly Trained
                <br />
                <span className="text-slate-600">Professionals</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Every member of the Sentrio team undergoes rigorous training and certification
                to ensure they meet the highest industry standards.
              </p>
              <div className="space-y-4">
                {[
                  "Fully licensed and certified",
                  "First Aid & CPR trained",
                  "Conflict resolution & de-escalation",
                  "Emergency response protocols",
                  "Customer service excellence",
                  "Ongoing professional development",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <span className="text-slate-300 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ backgroundColor: "#0A1224", borderTop: "1px solid #111D35" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to <span className="text-slate-400">Work With Us?</span>
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Join the hundreds of satisfied clients who trust Sentrio with their security needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-gray-900 font-semibold tracking-wide rounded hover:bg-slate-200 transition-all"
            >
              GET FREE CONSULTATION
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/careers"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white font-semibold tracking-wide rounded border border-slate-600 hover:bg-white/5 hover:border-slate-400 transition-all"
            >
              JOIN OUR TEAM
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default About
